import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { TabPane, Tabs } from '~/atomic/atom/Tabs';
import { ContestBanner, ContestBannerProps } from '~/atomic/page/contest/ContestBanner';
import { ContestBooks, ContestBooksProps } from '~/atomic/page/contest/ContestBooks';
import { ContestComments, ContestCommentsProps } from '~/atomic/page/contest/ContestComments';
import {
  ContestDescription,
  ContestDescriptionProps,
} from '~/atomic/page/contest/ContestDescription';
import { ContestResult, ContestResultProps } from '~/atomic/page/contest/ContestResult/ContestResult';
import {
  ContestSidebarBooks,
  ContestSidebarBooksProps,
} from '~/atomic/page/contest/ContestSidebarBooks';
import { isLaptopSizeSelector, isMobileSizeSelector } from '~/feature/media/windowSize.selector';
import { lessThan } from '~/lib/mediaQuery';
import { useAppSelector } from '~/store';

export interface ContestProps {
  bannerProps: ContestBannerProps;
  descriptionProps: ContestDescriptionProps;
  commentProps: ContestCommentsProps;
  contestBooksProps: ContestBooksProps;
  contestSidebarBooksProps: ContestSidebarBooksProps;
  contestResults?: ContestResultProps;
}

export const Contest = ({
  bannerProps,
  descriptionProps,
  commentProps,
  contestBooksProps,
  contestSidebarBooksProps,
  contestResults,
}: ContestProps) => {
  const router = useRouter();
  const [activeKey, changeActiveKey] = useState('1');
  const isMobileSize = useAppSelector(isMobileSizeSelector);
  const isLaptopSize = useAppSelector(isLaptopSizeSelector);

  useEffect(() => {
    if (router.asPath.includes('#author')) {
      changeActiveKey('3');
    }
  }, []);

  useEffect(() => {
    if (router.asPath.includes('#result')) {
      changeActiveKey('4');
    }
  }, []);

  const isShowSideBar = !isMobileSize && !isLaptopSize && !((activeKey === '3') || (activeKey === '4'));

  return (
    <SCContest>
      <ContestBanner {...bannerProps} />
      <SCContent>
        <Main data-is-show={!isShowSideBar}>
          <Tabs
            type="card"
            destroyInactiveTabPane
            onChange={changeActiveKey}
            activeKey={activeKey}
          >
            <TabPane
              tab="Описание"
              key="1"
            >
              <ContestDescription {...descriptionProps} />
            </TabPane>
            <TabPane
              tab="Обсуждение"
              key="2"
            >
              <ContestComments {...commentProps} />
            </TabPane>
            <TabPane
              tab="Участники"
              key="3"
              id="authors"
            >
              <ContestBooks {...contestBooksProps} />
            </TabPane>
            {contestResults ? (
              <TabPane
                tab="Оглашение результатов"
                key="4"
                id="result"
              >
                <ContestResult {...contestResults} {...contestBooksProps} />
              </TabPane>
            ) : null}
          </Tabs>
        </Main>
        {isShowSideBar ? (
          <Sidebar>
            <ContestSidebarBooks {...contestSidebarBooksProps} />
          </Sidebar>
        ) : null}
      </SCContent>
    </SCContest>
  );
};

const Main = styled.div`
  max-width: 740px;
  width: 100%;
  margin-right: 32px;
  
  &[data-is-show="true"] {
    max-width: 100%;
  }

  ${lessThan('md')} {
    margin-right: 0;
    max-width: none;
  }
`;

const Sidebar = styled.div`
  max-width: 370px;
  width: 100%;
  margin-top: 37px;
  flex-shrink: 0;
  
  ${lessThan('md')} {
    display: none;
  }
`;

const SCContest = styled.div`
  margin-top: 32px;
  margin-bottom: 32px;
  
  ${lessThan('container-width')} {
    padding-right: 16px;
    padding-left: 16px;
  }
  
  ${lessThan('sm')} {
    padding-right: 12px;
    padding-left: 12px;
  }
`;

const SCContent = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
`;
